import React, { useMemo } from 'react';

import { NewspaperIcon } from '@heroicons/react/outline';
import { FaRegFileAlt } from 'react-icons/fa';
import { useApiGet } from 'react-reqq-lite';
import dayjs from 'dayjs';

import CountCard from 'partial/dashboard/CountCard';
import PageWrapper from 'partial/components/PageWrapper';
import HistogramCard, {
  useHistogramCard,
} from 'partial/dashboard/HistogramCard';
import Card from 'partial/components/Card';
import BarChartSeries from 'modules/common/chart/BarChartSeries';
import FormInlineSelect from 'partial/form/FormInlineSelect';
import Button from 'partial/components/Button';
import { generateYears, usePersistState } from 'helper';
import { useDownloadFile } from 'partial/hooks';
import { PROFILE } from 'core-modules/profile/constants';

import {
  useBillerTransactionHistogram,
  useBillerCountCards,
  useBillerAmtHistogram,
  useBillerComparativeTransactions,
} from '../hooks';

const COMPARATIVE_ROWS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const COLORS = ['#D5D7E3', '#7E5EC5', '#B2D003'];

const txnHistogramOptions = [
  {
    id: 'transaction',
    label: 'Total Transactions',
    hook: useBillerTransactionHistogram,
    tableFormat: [
      {
        label: 'Month',
        key: 'label',
        id: 'label',
      },
      {
        label: 'No. of Transactions',
        key: (row) => <span>{row?.count}</span>,
        id: 'count',
      },
    ],
    defaultChart: 'table',
    defaultMode: 'year',
    switchMode: false,
    minDate: '01/01/2020',
    maxDate: `${dayjs().format('MM/DD/YYYY')}`,
    seriesName: 'Transaction',
  },
];

const amtHistogramOptions = [
  {
    id: 'amount',
    label: 'By Amount',
    hook: useBillerAmtHistogram,
    tableFormat: [
      {
        label: 'Month',
        key: 'label',
        id: 'label',
      },
      {
        label: 'Amount',
        key: (row) => <span>{row?.amount_human}</span>,
        id: 'amount_human',
      },
    ],
    defaultChart: 'table',
    defaultMode: 'year',
    switchMode: false,
    minDate: '01/01/2020',
    maxDate: `${dayjs().format('MM/DD/YYYY')}`,
    seriesName: 'Amount',
    lineChartOptions: {
      dataLabels: {
        formatter(val) {
          if (!val) return '';
          return [`$${val}`];
        },
      },
      tooltip: {
        y: {
          formatter(val) {
            return `$${val}`;
          },
        },
      },
    },
    barChartOptions: {
      dataLabels: {
        formatter(val) {
          if (!val) return '';
          return [`$${val}`];
        },
      },
      tooltip: {
        y: {
          formatter(val) {
            return `$${val}`;
          },
        },
      },
    },
  },
];

function BillerDashboardContainer() {
  const transactionHistogram = useHistogramCard(txnHistogramOptions);
  const amountHistogram = useHistogramCard(amtHistogramOptions);
  const defaultStartYear = new Date().getFullYear();
  const defaultEndYear = new Date().getFullYear() + 2;
  const [compareFilter, setCompareFilter] = usePersistState(
    'comparative-filter',
    {
      year_start: defaultStartYear.toString(),
      year_end: defaultEndYear.toString(),
    },
    {
      keepOnUnmount: false,
    }
  );
  const profile = useApiGet(PROFILE, {});
  const [downloadFile, isDownloadFileLoading] = useDownloadFile({
    downloadUrl: `wallet/v1/biller_mgmt/billers/${profile?.biller_id}/dashboard/comparative_export?year_start=${compareFilter?.year_start}&year_end=${compareFilter?.year_end}`,
    fileName: `TRANSACTIONS_LOGS_${compareFilter?.year_start}-${compareFilter?.year_end}`,
  });
  const compareActions = useMemo(() => {
    const yearsStart = generateYears(
      compareFilter?.year_start
        ? Number(compareFilter?.year_start)
        : new Date().getFullYear(),
      5,
      5
    );
    const yearStartOptions = yearsStart.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));
    const yearsEnd = generateYears(
      compareFilter?.year_start
        ? Number(compareFilter?.year_start) + 1
        : new Date().getFullYear() + 1,
      0,
      1
    );
    const yearEndOptions = yearsEnd.map((year) => ({
      label: year.toString(),
      value: year.toString(),
    }));

    return (
      <div className="flex items-center space-x-2">
        <FormInlineSelect
          name="year_start"
          onChange={setCompareFilter}
          onInput={({ target }) => {
            setCompareFilter({
              year_start: target?.value,
              year_end: '',
            });
          }}
          value={compareFilter.year_start}
          options={[{ label: '', value: '' }, ...yearStartOptions]}
          label=""
        />
        <span>To</span>
        <FormInlineSelect
          name="year_end"
          onChange={setCompareFilter}
          value={compareFilter.year_end}
          options={[{ label: '', value: '' }, ...yearEndOptions]}
          label=""
        />
        <Button
          size="sm"
          primary
          icon={FaRegFileAlt}
          onClick={downloadFile}
          disabled={
            isDownloadFileLoading ||
            !compareFilter?.year_start ||
            !compareFilter?.year_end
          }
        />
      </div>
    );
  }, [compareFilter, setCompareFilter, downloadFile, isDownloadFileLoading]);
  const [, compareGraph] = useBillerComparativeTransactions(compareFilter);

  return (
    <PageWrapper className="space-y-5 pb-5">
      <dl className="grid grid-cols-4 gap-x-8">
        <CountCard
          name="total_amount"
          label="Total Amount"
          icon={NewspaperIcon}
          hook={useBillerCountCards}
        />
        <CountCard
          name="total_transactions"
          label="Total Transactions"
          icon={NewspaperIcon}
          hook={useBillerCountCards}
        />
        <CountCard
          name="payouts"
          label="Payouts"
          icon={NewspaperIcon}
          hook={useBillerCountCards}
        />
        <CountCard
          name="accumulated_amount"
          label="Accumulated Amount"
          icon={NewspaperIcon}
          hook={useBillerCountCards}
        />
      </dl>
      <HistogramCard {...transactionHistogram} />
      <HistogramCard {...amountHistogram} />
      <Card
        label="Comparative Transaction List by Year"
        renderAction={compareActions}
      >
        <div className="flex flex-col pr-3 mb-4" style={{ height: 300 }}>
          <BarChartSeries
            labels={COMPARATIVE_ROWS}
            series={compareGraph?.graph || []}
            colors={COLORS}
          />
        </div>
      </Card>
    </PageWrapper>
  );
}

export default BillerDashboardContainer;
